/**
 * S3 — Client Chat (`/chat`). The single shared AM↔client thread. Newest at the
 * bottom; client bubbles in claret-tinted paper, AM bubbles plain paper;
 * tabular-numeral timestamps. Composer with send + (fail-closed) attach.
 *
 * Spec: teivor-build-spec/08-ui-spec.md §S3. Role: `client` (rep routed away).
 *
 * NOTES FOR ORCHESTRATOR:
 *  - The thread/messages API exposes no AM identity (name/avatar) — a generic
 *    "Your account manager" header is rendered; wire the AM principal to enrich.
 *  - The messages endpoint returns `hasAttachments` but NOT the attachment rows
 *    (id / filename / scan_status), and there is no binary-upload endpoint in
 *    the chat controller (only metadata `POST …/attachments` + a fail-closed
 *    download). So attachments render as a non-openable "scanned server-side"
 *    chip and the attach button is disabled. Embed attachment metadata in the
 *    messages payload + add an upload seam to light these up.
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  EmptyState,
  Skeleton,
  Textarea,
  toast,
  FileCertificateLine,
  MailSendLine,
  UploadLine,
  User3Fill,
} from '@teivor/shared-ui';
import { formatDateTime } from '@teivor/shared-utils';

import { PageContainer } from '../../../components/PageContainer';
import {
  useChatMessagesQuery,
  useChatThreadQuery,
  useMarkChatReadMutation,
  useSendMessageMutation,
} from '../queries';
import type { ChatMessage } from '../types';

function useLocale(): 'cs-CZ' | 'en-US' {
  const { i18n } = useTranslation();
  return i18n.language?.startsWith('en') ? 'en-US' : 'cs-CZ';
}

function AttachmentChip() {
  const { t } = useTranslation();
  // Fail-closed: with no scan_status from the API, the file is never openable.
  return (
    <span className="mt-2 inline-flex items-start gap-1.5 rounded-[var(--tvl-r2)] border border-[var(--tvl-hairline)] bg-[var(--tvl-raised)] px-2 py-1 text-[12px] leading-[16px] text-[var(--tvl-ink-2)]">
      <span className="mt-[1px] inline-flex shrink-0">
        <FileCertificateLine size={12} color="currentColor" aria-hidden />
      </span>
      <span>{t('com.chat.attachment_scanned', 'Příloha (kontrola na serveru) / Attachment (scanned server-side)')}</span>
    </span>
  );
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const locale = useLocale();
  const isClient = message.senderRole === 'client';
  return (
    <div className={isClient ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={[
          'max-w-[80%] rounded-[var(--tvl-r3)] px-3 py-2',
          isClient
            ? 'bg-[var(--tvl-selected)] text-[var(--tvl-ink)]'
            : 'bg-[var(--tvl-raised)] text-[var(--tvl-ink)] border border-[var(--tvl-hairline)]',
        ].join(' ')}
      >
        <p className="whitespace-pre-wrap break-words text-[14px] leading-[20px]">{message.body}</p>
        {message.hasAttachments ? <AttachmentChip /> : null}
        <p className="mt-1 text-[11px] leading-[14px] text-[var(--tvl-ink-3)] tabular-nums">
          {formatDateTime(message.createdAt, locale)}
        </p>
      </div>
    </div>
  );
}

export function ClientChatPage() {
  const { t } = useTranslation();
  const messagesQuery = useChatMessagesQuery();
  useChatThreadQuery(); // warms the thread (lazy-creates it server-side)
  const sendMutation = useSendMessageMutation();
  const markRead = useMarkChatReadMutation();

  const [draft, setDraft] = React.useState('');
  const bottomRef = React.useRef<HTMLDivElement | null>(null);

  // API returns newest-first; render oldest→newest (newest at the bottom).
  const ordered = React.useMemo(
    () => [...(messagesQuery.data ?? [])].reverse(),
    [messagesQuery.data],
  );

  React.useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [ordered.length]);

  // Advance the read marker to the newest message once loaded.
  const newestId = ordered.length ? ordered[ordered.length - 1].id : null;
  React.useEffect(() => {
    if (newestId) markRead.mutate({ upToMessageId: newestId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestId]);

  const onSend = React.useCallback(() => {
    const body = draft.trim();
    if (!body || sendMutation.isPending) return;
    sendMutation.mutate(
      { body },
      {
        onSuccess: () => setDraft(''),
        onError: () =>
          toast.error(t('com.chat.send_error', 'Zprávu se nepodařilo odeslat. Zkuste to znovu. / Could not send the message. Try again.')),
      },
    );
  }, [draft, sendMutation, t]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  return (
    <PageContainer
      width="narrow"
      // Chat owns the full frame height and cancels the shell's own vertical padding so the
      // composer sits on the bottom edge. A conversation stays at reading width on every
      // display: a 2400px-wide message row is harder to read, not easier.
      className="h-[calc(100dvh-3.5rem)] gap-0 -my-4 py-4 md:-my-6 md:py-6"
    >
      {/* AM identity header */}
      <header className="flex items-center gap-3 border-b border-[var(--tvl-hairline)] pb-3">
        <span
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--tvl-r-full)] bg-[var(--tvl-sunken)] text-[var(--tvl-ink-2)]"
          aria-hidden
        >
          <User3Fill size={18} color="currentColor" />
        </span>
        <div className="flex flex-col">
          <span className="text-[15px] font-semibold leading-[20px] text-[var(--tvl-ink)]">
            {t('com.chat.am_title', 'Váš account manager / Your account manager')}
          </span>
          <span className="text-[12px] leading-[16px] text-[var(--tvl-ink-3)]">
            {t('com.chat.subtitle', 'Přímá konverzace / Direct conversation')}
          </span>
        </div>
      </header>

      {/* Messages */}
      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        {messagesQuery.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2 self-end" />
            <Skeleton className="h-12 w-3/5" />
          </div>
        ) : messagesQuery.isError ? (
          <EmptyState
            variant="error-loading"
            title={t('com.chat.error', 'Konverzaci se nepodařilo načíst / Could not load the conversation')}
            action={
              <Button variant="secondary" size="sm" onClick={() => void messagesQuery.refetch()}>
                {t('common.retry', 'Zkusit znovu / Retry')}
              </Button>
            }
          />
        ) : ordered.length === 0 ? (
          <EmptyState
            variant="empty"
            title={t('com.chat.empty', 'Zatím žádné zprávy. Napište svému account managerovi. / No messages yet. Write to your account manager.')}
          />
        ) : (
          <div className="flex flex-col gap-3">
            {ordered.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="flex items-end gap-2 border-t border-[var(--tvl-hairline)] pt-3">
        <Button
          variant="tertiary"
          size="md"
          disabled
          title={t('com.chat.attach_soon', 'Přílohy budou brzy k dispozici / Attachments coming soon')}
          aria-label={t('com.chat.attach', 'Přiložit soubor / Attach file')}
        >
          <UploadLine size={18} color="currentColor" aria-hidden />
        </Button>

        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          rows={1}
          placeholder={t('com.chat.composer_placeholder', 'Napsat zprávu… / Write a message…')}
          aria-label={t('com.chat.composer_label', 'Napsat zprávu / Write a message')}
          className="flex-1 resize-none"
        />

        <Button
          variant="primary"
          size="md"
          onClick={onSend}
          loading={sendMutation.isPending}
          disabled={!draft.trim()}
          aria-label={t('com.chat.send', 'Odeslat / Send')}
        >
          <MailSendLine size={18} color="currentColor" aria-hidden />
        </Button>
      </div>
    </PageContainer>
  );
}
